import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { CardsService } from './cards.service';

export interface DashboardStats {
  totalCards: number;
  activeCards: number;
  totalBookmarks: number;

  cardMetrics?: {
    totalViews: number;
    clicks: number;
    saves: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(private cardsService: CardsService) {
    console.log('🔥 Dashboard service initialized');
  }

  /**
   * Fetch number of cards for the current user using CardsService state
   */
  getUserCardsCount(userId: string): Observable<number> {
    try {
      console.log('📊 Getting user cards count from CardsService state');
      
      return from(this.cardsService.getUserCards()).pipe(
        map(cards => {
          // getUserCards() already returns only the current user's cards from /api/cards/me
          console.log(`📈 Found ${cards.length} cards for user ${userId}`);
          return cards.length;
        }),
        catchError(error => {
          console.error('❌ Error fetching user cards from service:', error);
          return of(0);
        })
      );
    } catch (error) {
      console.error('❌ Error in getUserCardsCount:', error);
      return of(0);
    }
  }

  /**
   * Fetch card metrics (clicks, saves, visits) for all cards of the current user using CardsService state
   */
  getUserCardMetrics(userId: string): Observable<{ totalViews: number, clicks: number, saves: number }> {
    try {
      console.log('📊 Getting user card metrics from CardsService state');
      
      return from(this.cardsService.getUserCards()).pipe(
        map(cards => {
          let totalViews = 0;
          let clicks = 0;
          let saves = 0;

          cards.forEach(card => {
            const cardData = card as any;

            // Check for metrics in the card data
            totalViews += typeof cardData.visitevents === 'number' ? cardData.visitevents : 0;
            clicks += typeof cardData.clickevents === 'number' ? cardData.clickevents : 0;
            saves += typeof cardData.saveevents === 'number' ? cardData.saveevents : 0;
          });

          console.log(`📈 Card metrics for user ${userId}:`, { totalViews, clicks, saves });
          return { totalViews, clicks, saves };
        }),
        catchError(error => {
          console.error('❌ Error fetching card metrics from service:', error);
          return of({ totalViews: 0, clicks: 0, saves: 0 });
        })
      );
    } catch (error) {
      console.error('❌ Error in getUserCardMetrics:', error);
      return of({ totalViews: 0, clicks: 0, saves: 0 });
    }
  }

  /**
   * Get active cards count for the current user using CardsService state
   */
  getInactiveCardsCount(userId: string): Observable<number> {
  try {
    console.log('📊 Getting inactive cards count from CardsService state');

    return from(this.cardsService.getUserCards()).pipe(
      map(cards => {
        // Filter for cards that are NOT verified
        const inactiveCards = cards.filter(card => card.isVerified === false);
        console.log(`📈 Found ${inactiveCards.length} inactive cards for user ${userId}`);
        return inactiveCards.length;
      }),
      catchError(error => {
        console.error('❌ Error fetching inactive cards from service:', error);
        return of(0);
      })
    );
  } catch (error) {
    console.error('❌ Error in getInactiveCardsCount:', error);
    return of(0);
  }
}

  /**
   * Get comprehensive dashboard stats using CardsService state and Firestore for bookmarks
   */
  getDashboardStats(userId: string): Observable<DashboardStats> {
    try {
      console.log('📊 Getting comprehensive dashboard stats');
      
      return from(this.cardsService.getUserCards()).pipe(
        map(cards => {
          // Calculate card stats from CardsService state
          const totalCards = cards.length;
          const activeCards = cards.filter(card => card.isVerified === true).length;
          
          let totalViews = 0;
          let clicks = 0;
          let saves = 0;
          let totalBookmarks = 0;

          cards.forEach(card => {
            const cardData = card as any;
            totalViews += typeof cardData.visitevents === 'number' ? cardData.visitevents : 0;
            clicks += typeof cardData.clickevents === 'number' ? cardData.clickevents : 0;
            saves += typeof cardData.saveevents === 'number' ? cardData.saveevents : 0;
            totalBookmarks += typeof cardData.bookmarkevents === 'number' ? cardData.bookmarkevents : 0;
          });

          const cardMetrics = { totalViews, clicks, saves };

          console.log(`📈 Dashboard stats for user ${userId}: totalCards=${totalCards}, activeCards=${activeCards}, totalBookmarks=${totalBookmarks}`, cardMetrics);

          return {
            totalCards,
            activeCards,
            totalBookmarks,
            cardMetrics
          } as DashboardStats;
        }),
        catchError(error => {
          console.error('❌ Error fetching dashboard stats:', error);
          return of({
            totalCards: 0,
            activeCards: 0,
            totalBookmarks: 0,
            cardMetrics: { totalViews: 0, clicks: 0, saves: 0 }
          } as DashboardStats);
        })
      );
    } catch (error) {
      console.error('❌ Error in getDashboardStats:', error);
      return of({
        totalCards: 0,
        activeCards: 0,
        totalBookmarks: 0,
        cardMetrics: { totalViews: 0, clicks: 0, saves: 0 }
      } as DashboardStats);
    }
  }
}
